import React, { useContext, useState, useRef, useEffect } from "react";
import { Link } from "react-scroll";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../themeProvider";
import { motion, AnimatePresence } from "framer-motion";
import Hamburger from "hamburger-react";

const liveProjects = [
  {
    name: "Soccer Dashboard",
    description: "Real-Time Streaming & System Design",
    url: "https://realtimesoccer.com",
  },
  {
    name: "MovieFinder",
    description: "Gen-AI Movie Discovery Engine",
    url: "https://findmynextmovie.com",
  },
  {
    name: "BookFinder",
    description: "Book Recommendation Engine",
    url: "https://sarthak-jain.github.io/BookFinderApplication/",
  },
];

const Navbar = () => {
  const theme = useContext(ThemeContext);
  const [toggle, setToggle] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const demoRef = useRef(null);
  const darkMode = theme.state.darkMode;
  const navigate = useNavigate();
  const location = useLocation();
  const isMainPage = location.pathname === "/";

  const scrollLinks = [
    { name: "Home", route: "/" },
    { name: "About", route: "about" },
    { name: "Experience", route: "experience" },
    { name: "Projects", route: "projects" },
    { name: "Contact", route: "contact" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (demoRef.current && !demoRef.current.contains(e.target)) {
        setDemoOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toggleTheme() {
    if (darkMode === true) {
      theme.dispatch({ type: "LIGHTMODE" });
    } else {
      theme.dispatch({ type: "DARKMODE" });
    }
  }

  const handleScrollLink = (route) => {
    if (!isMainPage) {
      navigate("/");
      // Scroll will happen on page load via hash or react-scroll
    }
  };

  const linkClass = darkMode
    ? "block py-2 px-3 text-black hover:bg-blue-500 hover:text-white rounded-md"
    : "block py-2 px-3 text-white hover:bg-blue-500 hover:text-black rounded-md";

  const mobileLinkClass = darkMode
    ? "hover:bg-blue-500 text-black block px-3 py-2 rounded-md text-base font-medium mt-1 hover:text-white"
    : "hover:bg-blue-500 text-white block px-3 py-2 rounded-md text-base font-medium mt-1 hover:text-white";

  return (
    <>
      <nav
        className={
          darkMode
            ? "bg-white border-gray-200 z-50 shadow-lg md:px-8 px-1 fixed w-full top-0"
            : "bg-gray-700 border-gray-200 z-50 shadow-lg md:px-8 px-1 fixed w-full top-0"
        }
      >
        <div className="flex justify-between items-center py-2 md:py-4 md:px-2 pl-2 mx-auto">
          <div className="flex items-center cursor-pointer">
            <a
              href="/"
              className={
                darkMode
                  ? "text-xl font-medium text-decoration-none whitespace-nowrap text-black"
                  : "text-xl font-medium text-decoration-none whitespace-nowrap text-white"
              }
            >
              {`Sarthak Jain`}
            </a>
          </div>
          <div class="hidden justify-between items-center w-full md:flex md:w-auto ">
            <ul
              class={
                "flex flex-col mt-4 md:flex-row md:space-x-8 md:mt-0 md:text-md md:font-medium"
              }
            >
              {scrollLinks.map((el) => (
                <li key={el.name} className="cursor-pointer">
                  {isMainPage ? (
                    <Link
                      to={el.route}
                      activeClass={"text-white bg-blue-500"}
                      spy={true}
                      smooth={true}
                      className={linkClass}
                    >
                      {el.name}
                    </Link>
                  ) : (
                    <a
                      href={el.route === "/" ? "/" : `/#${el.route}`}
                      className={linkClass}
                      onClick={() => handleScrollLink(el.route)}
                    >
                      {el.name}
                    </a>
                  )}
                </li>
              ))}
              {/* Blog link — always a route navigation */}
              <li className="cursor-pointer">
                <a
                  href="/blog"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/blog");
                  }}
                  className={`${linkClass} ${
                    location.pathname.startsWith("/blog")
                      ? "text-white bg-blue-500"
                      : ""
                  }`}
                >
                  Blog
                </a>
              </li>
            </ul>
            <div className="relative ml-6" ref={demoRef}>
              <button
                onClick={() => setDemoOpen(!demoOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-blue-500 text-white hover:bg-blue-600 transition-colors whitespace-nowrap"
              >
                Live Demos
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${demoOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {demoOpen && (
                <div
                  className={`absolute right-0 mt-2 w-56 rounded-lg shadow-lg border z-50 ${
                    darkMode
                      ? "bg-white border-gray-200"
                      : "bg-gray-800 border-gray-600"
                  }`}
                >
                  {liveProjects.map((project, i) => (
                    <a
                      key={i}
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                        i === 0 ? "rounded-t-lg" : ""
                      } ${
                        i === liveProjects.length - 1 ? "rounded-b-lg" : ""
                      } ${
                        darkMode
                          ? "hover:bg-gray-100 text-gray-800"
                          : "hover:bg-gray-700 text-gray-200"
                      }`}
                      onClick={() => setDemoOpen(false)}
                    >
                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <div>
                        <div className="text-sm font-semibold">{project.name}</div>
                        <div className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                          {project.description}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div onClick={() => toggleTheme()}>
              {darkMode ? (
                <img
                  src="https://img.icons8.com/external-flaticons-flat-flat-icons/64/000000/external-sun-lighting-flaticons-flat-flat-icons.png"
                  className="w-6 ml-6 cursor-pointer hover:scale-1.50 block"
                  alt=""
                />
              ) : (
                <img
                  src="https://img.icons8.com/external-prettycons-lineal-color-prettycons/49/000000/external-moon-astrology-and-symbology-prettycons-lineal-color-prettycons.png"
                  className="w-6 ml-6 cursor-pointer hover:scale-1.50 block"
                  alt=""
                />
              )}
            </div>
          </div>

          <div className="flex md:hidden items-center">
            <div onClick={() => toggleTheme()}>
              {darkMode ? (
                <img
                  src="https://img.icons8.com/external-flaticons-flat-flat-icons/64/000000/external-sun-lighting-flaticons-flat-flat-icons.png"
                  className="w-6 mr-4 cursor-pointer hover:scale-1.50 block"
                  alt=""
                />
              ) : (
                <img
                  src="https://img.icons8.com/external-prettycons-lineal-color-prettycons/49/000000/external-moon-astrology-and-symbology-prettycons-lineal-color-prettycons.png"
                  alt=""
                  className="w-6 mr-4 cursor-pointer hover:scale-1.50 block"
                />
              )}
            </div>

            <Hamburger
              toggled={toggle}
              size={22}
              duration={0.8}
              distance={"lg"}
              toggle={setToggle}
              color={darkMode ? "#000000" : "#ffffff"}
            />
          </div>
        </div>
        {/* Mobile view nav bar */}
      </nav>
      <AnimatePresence>
        {toggle && (
          <motion.div
            initial={{ x: 100 }}
            animate={{ x: 0, transition: { type: "spring" } }}
            exit={{ x: 200, transition: { type: "spring" } }}
            className={
              darkMode
                ? "bg-white py-2 px-2 md:p-0 z-50 fixed top-16 mt-2 rounded-lg shadow-lg right-2 block w-40"
                : "bg-black py-2 px-2 md:p-0 z-50 fixed top-16 mt-2 rounded-lg shadow-lg right-2 block w-40"
            }
          >
            <ul class="md:hidden md:flex-row md:space-y-8 md:mt-0 md:text-md md:font-medium">
              {scrollLinks.map((el) => (
                isMainPage ? (
                  <Link
                    key={el.name}
                    to={el.route}
                    activeClass={"text-white bg-blue-500"}
                    className={mobileLinkClass}
                    spy={true}
                    smooth={true}
                    onClick={() => setToggle(false)}
                  >
                    <li>{el.name}</li>
                  </Link>
                ) : (
                  <a
                    key={el.name}
                    href={el.route === "/" ? "/" : `/#${el.route}`}
                    className={mobileLinkClass}
                    onClick={() => setToggle(false)}
                  >
                    <li>{el.name}</li>
                  </a>
                )
              ))}
              {/* Blog link in mobile */}
              <a
                href="/blog"
                className={mobileLinkClass}
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/blog");
                  setToggle(false);
                }}
              >
                <li>Blog</li>
              </a>
              {liveProjects.map((project, i) => (
                <a
                  key={i}
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    darkMode
                      ? "hover:bg-blue-500 text-black flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium mt-1 hover:text-white"
                      : "hover:bg-blue-500 text-white flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium mt-1 hover:text-white"
                  }
                  onClick={() => setToggle(false)}
                >
                  <li>{project.name}</li>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;